export const TILE_LOAD_HIDE_DELAY_MS = 400;

export type TileLoadSnapshot = {
  active: number;
  settled: number;
  visible: boolean;
};

const IDLE_SNAPSHOT: TileLoadSnapshot = {
  active: 0,
  settled: 0,
  visible: false,
};

let active = 0;
let settled = 0;
let visible = false;
let hideTimer: ReturnType<typeof setTimeout> | null = null;
let snapshot: TileLoadSnapshot = IDLE_SNAPSHOT;
const listeners = new Set<() => void>();

const publish = () => {
  const next: TileLoadSnapshot = { active, settled, visible };
  if (
    next.active === snapshot.active &&
    next.settled === snapshot.settled &&
    next.visible === snapshot.visible
  ) {
    return;
  }
  snapshot = next;
  listeners.forEach(listener => listener());
};

const clearHideTimer = () => {
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
};

export const beginTileRequest = (): (() => void) => {
  clearHideTimer();
  if (active === 0) {
    settled = 0;
  }
  active += 1;
  visible = true;
  publish();

  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    active = Math.max(0, active - 1);
    settled += 1;
    if (active === 0) {
      visible = true;
      publish();
      hideTimer = setTimeout(() => {
        hideTimer = null;
        if (active === 0) {
          settled = 0;
          visible = false;
          publish();
        }
      }, TILE_LOAD_HIDE_DELAY_MS);
      return;
    }
    publish();
  };
};

export const getTileLoadProgress = (): TileLoadSnapshot => snapshot;

export const subscribeTileLoadProgress = (
  listener: () => void,
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getTileLoadPercent = (
  progress: Pick<TileLoadSnapshot, 'active' | 'settled'>,
): number => {
  const total = progress.active + progress.settled;
  if (total <= 0) return 0;
  return Math.round((progress.settled / total) * 100);
};

export const formatTileLoadLabel = (
  progress: Pick<TileLoadSnapshot, 'active' | 'settled'>,
): string => {
  const total = progress.active + progress.settled;
  return `Loading ${progress.settled} of ${total}`;
};

export const resetTileLoadProgress = () => {
  clearHideTimer();
  active = 0;
  settled = 0;
  visible = false;
  snapshot = IDLE_SNAPSHOT;
  listeners.clear();
};
